/**
 * Shared routing of notification types across surfaces.
 * - Notification Centre: admin/user alerts and actionable inbox items.
 * - Activity Drawer: timeline-style friend/user activity feed.
 *
 * Bell unread (`GET /account/notifications/unread-count`) and the default notifications
 * list (`GET /account/notifications?surface=centre`) allowlist the same Centre types
 * (plus challenge/invite-like). Keep `NOTIFICATION_CENTER_TYPES` in sync with
 * `NOTIFICATION_CENTRE_TYPES` in `backend/db/notifications/notifications.js`.
 * Friends Activity uses `?surface=activity`.
 */

const NOTIFICATION_CENTER_TYPES = new Set([
  "welcome_bonus",
  "xp_week_podium",
  "friend_request",
  "friend_request_accepted",
  "now_friends",
  "squares_match_challenge",
  "squares_match_challenge_accepted",
  "bullion_match_challenge",
  "bullion_match_challenge_sent",
  "league_invite",
  "league_invite_sent",
  "league_member_joined",
  "league_member_joined_live",
  "league_starting",
  "league_cancelled",
  "league_finished",
  "bullion_battle_entered",
  "sixes_match_challenge",
  "sixes_match_challenge_sent",
  "trader_match_challenge",
  "trader_match_challenge_sent",
  "unlock_username_emoji",
  "coins_running_low",
  "promo",
  "admin_message",
  "wallet_grant",
  "sixes_card_ready",
  "sixes_cards_online",
]);

const ACTIVITY_DRAWER_TYPES = new Set([
  "xp_earned",
  "xp_week_podium",
  "friend_earned_xp",
  "friend_request_accepted",
  "now_friends",
  "squares_match_challenge",
  "squares_match_challenge_sent",
  "bullion_match_challenge",
  "bullion_match_challenge_sent",
  "league_invite",
  "league_invite_sent",
  "sixes_match_challenge",
  "sixes_match_challenge_sent",
  "trader_match_challenge",
  "trader_match_challenge_sent",
  "friend_started_playing_game",
  "friend_just_played_game",
  "friend_streak",
  "friend_pb",
]);

/** Friend duel invites — hide from Activity once history / finished. */
const FRIEND_CHALLENGE_NOTIFICATION_TYPES = new Set([
  "squares_match_challenge",
  "squares_match_challenge_sent",
  "bullion_match_challenge",
  "bullion_match_challenge_sent",
  "sixes_match_challenge",
  "sixes_match_challenge_sent",
  "trader_match_challenge",
  "trader_match_challenge_sent",
]);

function isTruthyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

/**
 * Keep legacy/flexible support for challenge/invite-like event names.
 */
function isChallengeLike(type) {
  if (!isTruthyString(type)) return false;
  const t = String(type).toLowerCase();
  return t.includes("challenge") || t.endsWith("_invite") || t.includes("game_invite");
}

export function shouldShowInNotificationCenter(notification) {
  const type = notification?.type;
  if (!isTruthyString(type)) return false;
  // Non-actionable / history challenges still list here (see notificationDisplay).
  // Activity / chips / My Games strips may hide them separately.
  if (NOTIFICATION_CENTER_TYPES.has(type)) return true;
  // Challenges/invites should always be visible in the Notifications Centre.
  return isChallengeLike(type);
}

export function isFriendChallengeNotificationType(type) {
  return typeof type === "string" && FRIEND_CHALLENGE_NOTIFICATION_TYPES.has(type);
}

/** Incoming invites only (not “You challenged…” sent copies). */
export function isIncomingFriendChallengeNotificationType(type) {
  return (
    type === "squares_match_challenge" ||
    type === "bullion_match_challenge" ||
    type === "sixes_match_challenge" ||
    type === "trader_match_challenge"
  );
}

/** True when the user entered the match/contest (soft-ack) — Centre history, no CTA. */
export function isFriendChallengeHistory(notification) {
  if (!isFriendChallengeNotificationType(notification?.type)) return false;
  if (notification?.challenge_history === true) return true;
  if (notification?.entity_ref == null || typeof notification.entity_ref !== "string") return false;
  try {
    const o = JSON.parse(notification.entity_ref);
    return o?.challenge_history === true;
  } catch {
    return false;
  }
}

/** Match/contest still open per API enrichment (ignores history flag). */
export function isFriendChallengeMatchOpen(notification) {
  return notification?.challenge_match_actionable !== false;
}

/**
 * Still offer PLAY / chips / Activity — open match and not yet entered (history).
 */
export function isFriendChallengePlayable(notification) {
  if (!isFriendChallengeNotificationType(notification?.type)) return false;
  if (isFriendChallengeHistory(notification)) return false;
  return isFriendChallengeMatchOpen(notification);
}

/**
 * Challenge rows leave Activity after enter-match history or when the match is closed.
 * Sent copies also leave Activity once read. Finished / history rows remain in Notification Centre
 * (see shouldHideFriendChallengeFromNotificationCenter).
 */
export function isAcknowledgedFriendChallenge(notification) {
  if (!isFriendChallengeNotificationType(notification?.type)) return false;
  if (isFriendChallengeHistory(notification)) return true;
  if (!notification?.read_at) return false;
  const incoming = isIncomingFriendChallengeNotificationType(notification.type);
  if (incoming) {
    // Still playable → keep listed (badge may be cleared via read_at; friends chips use read_at).
    return !isFriendChallengeMatchOpen(notification);
  }
  return true;
}

/**
 * Notification Centre keeps:
 * - enter-match history rows (until Clear All)
 * - still-playable challenges even after read (sent copies clear badge on open; row stays)
 * Finished + read rows hide.
 */
export function shouldHideFriendChallengeFromNotificationCenter(notification) {
  if (!isFriendChallengeNotificationType(notification?.type)) return false;
  if (isFriendChallengeHistory(notification)) return false;
  if (isFriendChallengePlayable(notification)) return false;
  // Finished / closed: keep while unread (finished copy); hide once read.
  return Boolean(notification?.read_at);
}

export function shouldShowInActivityDrawer(notification) {
  const type = notification?.type;
  if (!isTruthyString(type)) return false;
  // Keep Activity focused on actionable invites; history / finished stay in Notification Centre.
  if (isFriendChallengeNotificationType(type)) {
    if (!isFriendChallengePlayable(notification)) return false;
  }
  if (ACTIVITY_DRAWER_TYPES.has(type)) return true;
  // Keep the activity stream focused; treat only explicit activity types here.
  return false;
}
